import axios, { AxiosResponse, AxiosRequestConfig, AxiosError } from "axios"
import store from "@/store"
import { article } from "@/store/article"
import { adminUser } from "@/store/adminUser"
import router from "./router"
import Vue from "vue"
import { DirectUpload } from "activestorage"
import { toOriginalStatusInternal } from "./mixins/status-mixin"
import {
  AdminUser,
  AdsTxt,
  AlertLevel,
  AnalyticsGroup,
  AppAffiliate,
  AppAffiliateTemplate,
  Article,
  ArticlePostscript,
  ArticleSearchMaster,
  ArticleSearchParams,
  ArticleType,
  BannerType,
  BlobWithId,
  Category,
  Curator,
  MaxPriority,
  ImageUrl,
  InlineAdvertisement,
  PopupHtml,
  Postscript,
  Preview,
  RecommendCollection,
  RefetchBookCover,
  ResponseArticle,
  SpecialFeature,
  SponsoredLink,
  SubCategory,
  Version,
} from "types/index"

export const client = axios.create({
  baseURL: `${process.env.VUE_APP_API_SCHEME}://${process.env.VUE_APP_API_HOST}:${process.env.VUE_APP_API_PORT}`,
})

client.interceptors.request.use((config: AxiosRequestConfig) => {
  if (store.state.authToken) {
    Object.assign(config.headers, {
      Authorization: `Bearer ${store.state.authToken}`,
    })
  }
  return config
})

client.interceptors.response.use(
  (response) => {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    const token = response.headers.authorization
    if (token) {
      store.commit("updateAuthToken", token)
    }
    return response
  },
  async (error: AxiosError) => {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    if (error.response?.status === 401) {
      // unauthorized
      Vue.toasted.clear()
      Vue.toasted.error("ログイン有効期限切れです")
      await store.dispatch("logout")
      router.push({ path: "/login" })
    }
    return Promise.reject(error)
  }
)

export default {
  createOrUpdateArticleParams() {
    const ret = {}

    if (adminUser.state.role === "writer") {
      Object.assign(ret, { writerId: adminUser.state.id })
    }
    if (article.state.writer && article.state.writer.id) {
      Object.assign(ret, { writerId: article.state.writer.id })
    }
    if (article.state.editor && article.state.editor.id) {
      Object.assign(ret, { editorId: article.state.editor.id })
    }
    if (article.state.articleType && article.state.articleType.id) {
      Object.assign(ret, { articleTypeId: article.state.articleType.id })
    }
    if (article.state.bannerType && article.state.bannerType.id) {
      Object.assign(ret, { bannerTypeId: article.state.bannerType.id })
    } else {
      Object.assign(ret, { bannerTypeId: null })
    }
    if (
      article.state.articleType &&
      article.state.articleType.value === "wellKnown"
    ) {
      if (article.state.curator && article.state.curator.id) {
        Object.assign(ret, { curatorId: article.state.curator.id })
      } else {
        // このケースはエラーケースだが、サーバ側でバリデーションチェックさせるためにあえてnullを送っている
        Object.assign(ret, { curatorId: null })
      }
    }
    if (article.state.analyticsGroup) {
      // id: nullの場合は未選択
      Object.assign(ret, { analyticsGroupId: article.state.analyticsGroup.id })
    }
    if (article.state.overlayAppAffiliateTemplate) {
      // id: nullの場合は未選択
      Object.assign(ret, {
        overlayAppAffiliateTemplateId:
          article.state.overlayAppAffiliateTemplate.id,
      })
    }
    if (article.state.eyecatch.url) {
      Object.assign(ret, { eyecatch: { url: article.state.eyecatch.url } })
    }
    if (article.state.thumbnail.url) {
      Object.assign(ret, { thumbnail: { url: article.state.thumbnail.url } })
    }
    Object.assign(ret, {
      title: article.state.title,
      rssTitle: article.state.rssTitle,
      rssCategory: article.state.rssCategory,
      overview: article.state.overview,
      editorData: article.state.editorData,
      status: toOriginalStatusInternal(article.state.status),
      writerDisplayEnabled: article.state.writerDisplayEnabled,
      isAdvertisementEnabled: article.state.isAdvertisementEnabled,
      isNoIndex: article.state.isNoIndex,
      isNoFollow: article.state.isNoFollow,
      showRss: article.state.showRss,
      canonicalUrl: article.state.canonicalUrl,
      publishedAt: article.state.publishedAt,
      endOfPublishedAt: article.state.endOfPublishedAt,
      rssPublishedAt: article.state.rssPublishedAt,
      mainCategoryId: article.state.mainCategoryId,
      mainSubCategoryId: article.state.mainSubCategory
        ? article.state.mainSubCategory.id
        : null,
      enableDisplayUpdatedAt: article.state.enableDisplayUpdatedAt,
      inlineAdvertisementType: article.state.inlineAdvertisementType,
      relatedArticleIds:
        article.state.relatedArticles === null
          ? null
          : article.state.relatedArticles.map(
              (relatedArticle) => relatedArticle.id
            ),
      relatedCategoryIds:
        article.state.categories === null
          ? null
          : article.state.categories.map((category) => category.id),
      relatedSubCategoryIds:
        article.state.subCategories === null
          ? null
          : article.state.subCategories.map((subCategory) => subCategory.id),
    })

    return ret
  },

  // 管理ユーザログインAPI
  login(
    email: string,
    password: string
  ): Promise<AxiosResponse<{ jwt: string }>> {
    return client.post("/auth", { auth: { email, password } })
  },
  getArticle(id: number): Promise<AxiosResponse<ResponseArticle["get"]>> {
    return client.get(`/admin/articles/${id}`)
  },
  getArticles(
    params?: ArticleSearchParams
  ): Promise<AxiosResponse<ResponseArticle["index"]>> {
    return client.get("/admin/articles", { params })
  },
  createArticle(): Promise<AxiosResponse<Omit<ResponseArticle["post"], "id">>> {
    const params = this.createOrUpdateArticleParams()
    return client.post("/admin/articles", { article: params })
  },
  updateArticle(): Promise<AxiosResponse<ResponseArticle["patch"]>> {
    const params = this.createOrUpdateArticleParams()
    return client.patch(`/admin/articles/${article.state.id}`, {
      article: params,
    })
  },
  applyPostscriptByArticle(
    id: number,
    editorData: string
  ): Promise<AxiosResponse<ArticlePostscript["patch"]>> {
    return client.patch(`/admin/articles/${id}/postscripts`, {
      article: { editorData },
    })
  },
  getArticleTypes(): Promise<AxiosResponse<ArticleType[]>> {
    return client.get("/admin/article_types")
  },
  getBannerTypes(): Promise<AxiosResponse<BannerType[]>> {
    return client.get("/admin/banner_types")
  },
  postPreview(
    deviceType: string,
    expiredTime?: number
  ): Promise<AxiosResponse<Preview["data"]>> {
    const articleParams = this.createOrUpdateArticleParams()
    return client.post(`/preview`, {
      deviceType,
      expiredTime,
      article: articleParams,
    })
  },
  getAdminUsers(
    role: string,
    name = "",
    page = 1,
    per = 10,
    sortBy = "id",
    descending = true
  ): Promise<AxiosResponse<AdminUser["index"]>> {
    return client.get(`/admin/users`, {
      params: {
        role,
        name,
        page,
        per,
        sortBy,
        descending,
      },
    })
  },
  // 引数にauthTokenがない場合,
  // すでにログイン済みの想定で、requestのinterceptorsでAuthorizationヘッダを仕込むことになるはず
  getAdminUserByAuthToken(
    authToken?: string
  ): Promise<AxiosResponse<AdminUser["get"]>> {
    if (authToken) {
      return client.get("/admin/user", {
        headers: { Authorization: `Bearer ${authToken}` },
      })
    } else {
      return client.get("/admin/user")
    }
  },
  getAdminUserById(id: number): Promise<AxiosResponse<AdminUser["get"]>> {
    return client.get(`/admin/user`, { params: { id } })
  },
  createAdminUser(
    email: string,
    name: string,
    password: string,
    position: string,
    description: string,
    role: string,
    thumbnail: string,
    thumbnailName: string,
    twitterUrl: string | null,
    instagramUrl: string | null,
    youtubeUrl: string | null,
    linkUrl: string | null
  ): Promise<Omit<AxiosResponse<AdminUser["post"]>, "id">> {
    const params = {
      user: {
        email,
        name,
        password,
        position,
        description,
        role,
        twitterUrl,
        instagramUrl,
        youtubeUrl,
        linkUrl,
      },
      thumbnail: { name: thumbnailName, image: thumbnail },
    }
    return client.post("/admin/users", params)
  },
  updateAdminUser(
    id: number,
    email: string,
    name: string,
    position: string,
    description: string,
    password: string,
    thumbnail: string,
    thumbnailName: string,
    twitterUrl: string | null,
    instagramUrl: string | null,
    youtubeUrl: string | null,
    linkUrl: string | null
  ): Promise<AxiosResponse<AdminUser["patch"]>> {
    const params = {
      user: {
        email,
        name,
        position,
        description,
        password,
        twitterUrl,
        instagramUrl,
        youtubeUrl,
        linkUrl,
      },
      thumbnail: { name: thumbnailName, image: thumbnail },
    }
    return client.patch(`/admin/users/${id}`, params)
  },
  deleteAdminUser(id: number): Promise<AxiosResponse<AdminUser["data"]>> {
    return client.delete(`/admin/users/${id}`)
  },
  adminSearchMasters(): Promise<AxiosResponse<ArticleSearchMaster>> {
    // ログインユーザーに従って必要なマスターデータを取得する
    return client.get("/admin/search_masters")
  },
  createDirectUpload(file: File): Promise<BlobWithId | Error> {
    const url = `${client.defaults.baseURL}/admin/direct_uploads`
    const upload = new DirectUpload(file, url, {
      directUploadWillCreateBlobWithXHR: (xhr) => {
        xhr.setRequestHeader("Authorization", `Bearer ${store.state.authToken}`)
      },
    })
    return new Promise((resolve, reject) => {
      upload.create((err, blob) => {
        if (err) {
          return reject(err)
        } else {
          return resolve(blob as BlobWithId)
        }
      })
    })
  },
  getImageUrl(id: number): Promise<AxiosResponse<ImageUrl>> {
    return client.get(`/admin/blobs/${id}`)
  },
  changeStatus(
    id: number,
    status: string
  ): Promise<AxiosResponse<{ article: Article }>> {
    const params = {
      article: { status },
    }
    return client.patch(`/admin/articles/${id}/status`, params)
  },
  getVersions(
    articleId: number,
    pagination: {
      page: number
      rowsPerPage: number
      sortBy: string
      descending: boolean
    }
  ): Promise<AxiosResponse<Version["index"]>> {
    return client.get(`/admin/articles/${articleId}/versions`, {
      params: pagination,
    })
  },
  getVersion(
    articleId: number,
    versionId: number
  ): Promise<AxiosResponse<Version["get"]>> {
    return client.get(`/admin/articles/${articleId}/versions/${versionId}`)
  },
  getCategories(params?: {
    search: string
    page: number
    per: number
    sortBy: string
    descending: boolean
    includeDisabled: boolean
    fields: string[]
  }): Promise<AxiosResponse<Category["index"]>> {
    return client.get(`/admin/categories`, { params })
  },
  getCategory(id: number): Promise<AxiosResponse<Category["get"]>> {
    return client.get(`/admin/categories/${id}`)
  },
  createCategory(data: {
    name: string
    urlKey: string
    isEnabled: boolean
    priority: number
    specialFeatures: SpecialFeature["view"][]
    bannerTypeId?: BannerType["id"]
  }): Promise<AxiosResponse<Category["post"]>> {
    const params = { category: data }
    return client.post(`/admin/categories`, params)
  },
  updateCategory(
    data: Category["view"]
  ): Promise<AxiosResponse<Category["patch"]>> {
    const params = { category: data }
    return client.patch(`/admin/categories/${data.id}`, params)
  },
  deleteCategory(id: number): Promise<AxiosResponse<Category["data"]>> {
    return client.delete(`/admin/categories/${id}`)
  },
  getMaxPriorityForCategories(): Promise<AxiosResponse<MaxPriority>> {
    return client.get("/admin/max_priority/categories")
  },
  getCurators(params?: {
    name: string
    page: number
    per: number
    sortBy: string
    descending: boolean
  }): Promise<AxiosResponse<Curator["index"]>> {
    return client.get(`/admin/curators`, { params })
  },
  getCurator(id: number): Promise<AxiosResponse<Curator["get"]>> {
    return client.get(`/admin/curators/${id}`)
  },
  createCurator(
    curator: Omit<Curator["view"], "id">
  ): Promise<AxiosResponse<Curator["post"]>> {
    return client.post(`/admin/curators`, { curator })
  },
  updateCurator(
    curator: Curator["view"]
  ): Promise<AxiosResponse<Curator["patch"]>> {
    return client.patch(`/admin/curators/${curator.id}`, { curator })
  },
  deleteCurator(id: number): Promise<AxiosResponse<Curator["data"]>> {
    return client.delete(`/admin/curators/${id}`)
  },
  getSubCategories(params?: {
    categoryIds: number[] | null
    excludeIds: number[] | null
    name: string
    sortBy: string
    page: number
    per: number
    descending: boolean
    includeDisabled: boolean
    fields: string[]
    withCategory?: boolean
    breadcrumbStatuses?: string[]
  }): Promise<AxiosResponse<SubCategory["index"]>> {
    return client.get("/admin/sub_categories", { params })
  },
  getSubCategory(
    id: number,
    withCategory?: boolean
  ): Promise<AxiosResponse<SubCategory["get"]>> {
    return client.get(`/admin/sub_categories/${id}`, {
      params: { withCategory },
    })
  },
  createSubCategory(
    subCategory: {
      categoryId: number
      name: string
      urlKey: string
      description: string | null
      breadcrumbStatus: number
      order: number
      isEnabled: boolean
      bannerFilename: string
      bannerContent: string
    },
    withCategory?: boolean
  ): Promise<AxiosResponse<Omit<SubCategory["post"], "id">>> {
    return client.post(`/admin/sub_categories`, { subCategory, withCategory })
  },
  updateSubCategory(
    subCategory: {
      id: number
      categoryId: number
      name: string
      urlKey: string
      description: string | null
      breadcrumbStatus: number
      order: number
      isEnabled: boolean
      bannerFilename: string
      bannerContent: string
      showOnTop: boolean
    },
    withCategory?: boolean
  ): Promise<AxiosResponse<SubCategory["patch"]>> {
    return client.patch(`/admin/sub_categories/${subCategory.id}`, {
      subCategory,
      withCategory,
    })
  },
  getMaxOrderForSubCategory(
    categoryId: number
  ): Promise<AxiosResponse<{ maxOrder: number }>> {
    return client.get(
      `/admin/max_order/categories/${categoryId}/sub_categories`
    )
  },
  getAppAffiliates(
    name: string,
    label: string,
    filterIsEnabled: string,
    sortBy: string,
    descending: boolean,
    page: number,
    per: number
  ): Promise<AxiosResponse<AppAffiliate["index"]>> {
    const params = {
      name,
      label,
      filterIsEnabled,
      sortBy,
      descending,
      page,
      per,
    }
    return client.get("/admin/app_affiliates", { params })
  },
  createAppAffiliate(
    data: Omit<AppAffiliate["data"], "id">
  ): Promise<AxiosResponse<AppAffiliate["post"]>> {
    const params = {
      appAffiliate: {
        name: data.name,
        label: data.label,
        isEnabled: data.isEnabled,
      },
    }
    return client.post("/admin/app_affiliates", params)
  },
  updateAppAffiliate(
    data: AppAffiliate["data"]
  ): Promise<AxiosResponse<AppAffiliate["patch"]>> {
    const params = {
      appAffiliate: {
        name: data.name,
        label: data.label,
        isEnabled: data.isEnabled,
      },
    }
    return client.patch(`/admin/app_affiliates/${data.id}`, params)
  },
  getAppAffiliateTemplates(
    name: string,
    adText: string,
    filterIsEnabled: AppAffiliateTemplate["data"]["filterIsEnabled"],
    appAffiliateId: number | null,
    sortBy: string,
    descending: boolean,
    page: number,
    per: number,
    fields: string[],
    withAppAffiliate?: boolean
  ): Promise<AxiosResponse<AppAffiliateTemplate["index"]>> {
    const params = {
      name,
      adText,
      filterIsEnabled,
      appAffiliateId,
      sortBy,
      descending,
      page,
      per,
      fields,
      withAppAffiliate,
    }
    return client.get("/admin/app_affiliate_templates", { params })
  },
  getAppAffiliateTemplate(
    id: number
  ): Promise<AxiosResponse<AppAffiliateTemplate["get"]>> {
    return client.get(`/admin/app_affiliate_templates/${id}`)
  },
  createAppAffiliateTemplate(
    appAffiliateTemplate: Omit<AppAffiliateTemplate["params"], "id">
  ): Promise<AxiosResponse<AppAffiliateTemplate["post"]>> {
    return client.post("/admin/app_affiliate_templates", {
      appAffiliateTemplate,
    })
  },
  updateAppAffiliateTemplate(
    appAffiliateTemplate: AppAffiliateTemplate["params"]
  ): Promise<AxiosResponse<AppAffiliateTemplate["patch"]>> {
    return client.patch(
      `/admin/app_affiliate_templates/${appAffiliateTemplate.id}`,
      { appAffiliateTemplate }
    )
  },
  getPostscripts(): Promise<
    AxiosResponse<Postscript["indexWithoutPagination"]>
  > {
    return client.get(`/admin/postscripts`)
  },
  createPostscript(
    data: Omit<Postscript["view"], "id">
  ): Promise<AxiosResponse<Postscript["post"]>> {
    const params = {
      postscript: data,
    }
    return client.post(`/admin/postscripts`, params)
  },
  updatePostscript(
    data: Postscript["view"]
  ): Promise<AxiosResponse<Postscript["patch"]>> {
    const params = {
      postscript: data,
    }
    return client.patch(`/admin/postscripts/${data.id}`, params)
  },
  deletePostscript(id: number): Promise<AxiosResponse<Postscript["data"]>> {
    return client.delete(`/admin/postscripts/${id}`)
  },
  getAlertLevels(): Promise<AxiosResponse<AlertLevel[]>> {
    return client.get(`/admin/postscripts/alert_levels`)
  },
  getPopupHTML(): Promise<AxiosResponse<PopupHtml["indexWithoutPagination"]>> {
    return client.get(`/admin/postscripts/popup_html`)
  },
  getAnalyticsGroups(): Promise<
    AxiosResponse<AnalyticsGroup["indexWithoutPagination"]>
  > {
    return client.get(`/admin/analytics_groups`)
  },
  getAnalyticsGroup(
    id: AnalyticsGroup["data"]["id"]
  ): Promise<AxiosResponse<AnalyticsGroup["data"]>> {
    return client.get(`/admin/analytics_groups/${id}`)
  },
  createAnalyticsGroup(
    data: Omit<AnalyticsGroup["data"], "id">
  ): Promise<AxiosResponse<AnalyticsGroup["post"]>> {
    const params = { analyticsGroup: data }
    return client.post(`/admin/analytics_groups`, params)
  },
  updateAnalyticsGroup(
    data: AnalyticsGroup["data"]
  ): Promise<AxiosResponse<AnalyticsGroup["patch"]>> {
    const params = { analyticsGroup: data }
    return client.patch(`/admin/analytics_groups/${data.id}`, params)
  },
  deleteAnalyticsGroup(
    id: AnalyticsGroup["data"]["id"]
  ): Promise<AxiosResponse<AnalyticsGroup["data"]>> {
    return client.delete(`/admin/analytics_groups/${id}`)
  },
  getSponsoredLink(id: number): Promise<AxiosResponse<SponsoredLink["get"]>> {
    return client.get(`/admin/sponsored_links/${id}`)
  },
  getSponsoredLinks(): Promise<
    AxiosResponse<SponsoredLink["indexWithoutPagination"]>
  > {
    return client.get(`/admin/sponsored_links`)
  },
  createSponsoredLink(
    sponsoredLink: Omit<SponsoredLink["data"], "id">
  ): Promise<AxiosResponse<SponsoredLink["post"]>> {
    return client.post(`/admin/sponsored_links`, { sponsoredLink })
  },
  updateSponsoredLink(
    sponsoredLink: SponsoredLink["data"]
  ): Promise<AxiosResponse<SponsoredLink["patch"]>> {
    return client.patch(`/admin/sponsored_links/${sponsoredLink.id}`, {
      sponsoredLink,
    })
  },
  deleteSponsoredLink(
    id: number
  ): Promise<AxiosResponse<SponsoredLink["data"]>> {
    return client.delete(`/admin/sponsored_links/${id}`)
  },
  getAdsTxt(): Promise<AxiosResponse<AdsTxt>> {
    return client.get(`/admin/ads_txt`)
  },
  updateAdsTxt(adsTxt: string): Promise<AxiosResponse<AdsTxt>> {
    return client.patch(`/admin/ads_txt`, { adsTxt })
  },
  downloadArticles(
    params: ArticleSearchParams
  ): Promise<AxiosResponse<string>> {
    return client.get(`/admin/download_articles`, { params })
  },
  changePublishDate(
    articleId: number,
    publishedAt: string,
    endOfPublishedAt: string
  ): Promise<AxiosResponse<ResponseArticle["patch"]>> {
    return client.patch(`/admin/articles/${articleId}/publish_date`, {
      article: { publishedAt, endOfPublishedAt },
    })
  },
  getInlineAdvertisement(
    id: number
  ): Promise<AxiosResponse<InlineAdvertisement["get"]>> {
    return client.get(`/admin/inline_advertisements/${id}`)
  },
  getInlineAdvertisements(): Promise<
    AxiosResponse<InlineAdvertisement["indexWithoutPagination"]>
  > {
    return client.get(`/admin/inline_advertisements`)
  },
  createInlineAdvertisement(inlineAdvertisement: {
    script: string
    description: string
    isGoogle: boolean
  }): Promise<AxiosResponse<InlineAdvertisement["post"]>> {
    return client.post(`/admin/inline_advertisements`, { inlineAdvertisement })
  },
  updateInlineAdvertisement(inlineAdvertisement: {
    id: number
    script: string
    description: string
    isGoogle: boolean
  }): Promise<AxiosResponse<InlineAdvertisement["patch"]>> {
    return client.patch(
      `/admin/inline_advertisements/${inlineAdvertisement.id}`,
      { inlineAdvertisement }
    )
  },
  deleteInlineAdvertisement(
    id: number
  ): Promise<AxiosResponse<InlineAdvertisement["data"]>> {
    return client.delete(`/admin/inline_advertisements/${id}`)
  },
  getSpecialFeatures(specialFeature: {
    isEnabled: boolean | undefined | null
    showOnTop: boolean | undefined | null
    showTitle: boolean | undefined | null
    page: number | undefined
    per: number | undefined
    sortBy: string | undefined
    descending: boolean
    excludeSpecialFeatureIds: number[]
    includes: string[] | undefined
  }): Promise<AxiosResponse<SpecialFeature["index"]>> {
    return client.get(`/admin/special_features`, { params: specialFeature })
  },
  getSpecialFeature(id: number): Promise<AxiosResponse<SpecialFeature["get"]>> {
    return client.get(`/admin/special_features/${id}`)
  },
  createSpecialFeature(specialFeature: {
    title: string
    urlKey: string
    overview: string
    priority: number
    showOnTop: boolean
    showTitle: boolean
    isEnabled: boolean
    banner: { filename: string; content: string }
    articleIds: number[]
  }): Promise<AxiosResponse<SpecialFeature["post"]>> {
    return client.post(`/admin/special_features`, { specialFeature })
  },
  updateSpecialFeature(specialFeature: {
    id: number
    title: string
    urlKey: string
    overview: string
    priority: number
    showOnTop: boolean
    showTitle: boolean
    isEnabled: boolean
    banner: { filename: string; content: string }
    articleIds: number[]
  }): Promise<AxiosResponse<SpecialFeature["patch"]>> {
    return client.patch(`/admin/special_features/${specialFeature.id}`, {
      specialFeature,
    })
  },
  deleteSpecialFeature(
    id: number
  ): Promise<AxiosResponse<SpecialFeature["data"]>> {
    return client.delete(`/admin/special_features/${id}`)
  },
  getMaxPriorityForSpecialFeatures(): Promise<AxiosResponse<MaxPriority>> {
    return client.get("/admin/max_priority/special_features")
  },
  getRecommendCollections(recommendCollection: {
    showOnTop: boolean | undefined | null
    isEnabled: boolean | undefined | null
    page: number | undefined
    per: number | undefined
    sortBy: string | undefined
    descending: boolean
    excludedIds: number[]
    includes: string[] | undefined
  }): Promise<AxiosResponse<RecommendCollection["index"]>> {
    return client.get(`/admin/recommend_collections`, {
      params: recommendCollection,
    })
  },
  getRecommendCollection(
    id: number
  ): Promise<AxiosResponse<RecommendCollection["get"]>> {
    return client.get(`/admin/recommend_collections/${id}`)
  },
  createRecommendCollection(recommendCollection: {
    title: string
    priority: number
    showOnTop: boolean
    isEnabled: boolean
    articleIds: number[]
  }): Promise<AxiosResponse<RecommendCollection["post"]>> {
    return client.post(`/admin/recommend_collections`, { recommendCollection })
  },
  updateRecommendCollection(recommendCollection: {
    id: number
    title: string
    priority: number
    showOnTop: boolean
    isEnabled: boolean
    articleIds: number[]
  }): Promise<AxiosResponse<RecommendCollection["patch"]>> {
    return client.patch(
      `/admin/recommend_collections/${recommendCollection.id}`,
      { recommendCollection }
    )
  },
  deleteRecommendCollection(
    id: number
  ): Promise<AxiosResponse<RecommendCollection["data"]>> {
    return client.delete(`/admin/recommend_collections/${id}`)
  },
  getMaxPriorityForRecommendCollections(): Promise<AxiosResponse<MaxPriority>> {
    return client.get("/admin/max_priority/recommend_collections")
  },
  putRefetchBookCover(isbn: string): Promise<AxiosResponse<RefetchBookCover>> {
    return client.put(`/admin/books/refetch_cover/${isbn}`)
  },
}
